import {
  ANNOTATION_LOCATION,
  stringifyEntityRef,
} from "@backstage/catalog-model";
import { HeaderIconLinkRow } from "@backstage/core-components";
import { alertApiRef, errorApiRef, useApi } from "@backstage/core-plugin-api";
import {
  ScmIntegrationIcon,
  scmIntegrationsApiRef,
} from "@backstage/integration-react";
import { AboutContent } from "@backstage/plugin-catalog";
import { catalogEntityRefreshPermission } from "@backstage/plugin-catalog-common/alpha";
import {
  catalogApiRef,
  getEntitySourceLocation,
  useEntity,
} from "@backstage/plugin-catalog-react";
import { useEntityPermission } from "@backstage/plugin-catalog-react/alpha";
import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";
import CardHeader from "@material-ui/core/CardHeader";
import Divider from "@material-ui/core/Divider";
import IconButton from "@material-ui/core/IconButton";
import { makeStyles } from "@material-ui/core/styles";
import CachedIcon from "@material-ui/icons/Cached";
import EditIcon from "@material-ui/icons/Edit";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";
import { useCallback } from "react";
import { useNavigate } from "react-router-dom";

const useStyles = makeStyles({
  gridItemCard: {
    display: "flex",
    flexDirection: "column",
    height: "calc(100% - 10px)",
    marginBottom: "10px",
  },
  gridItemCardContent: {
    flex: 1,
  },
});

type Variant = "gridItem" | "fullHeight";

interface Props {
  variant?: Variant;
}

/**
 * Tarjeta About para entidades System con el lápiz de edición habilitado.
 * Al pulsar el lápiz navega al template edit-system con los campos pre-rellenados.
 */
export const EntityAboutSystemCard = ({ variant = "gridItem" }: Props) => {
  const classes = useStyles();
  const { entity } = useEntity();
  const navigate = useNavigate();
  const catalogApi = useApi(catalogApiRef);
  const alertApi = useApi(alertApiRef);
  const errorApi = useApi(errorApiRef);
  const scmIntegrationsApi = useApi(scmIntegrationsApiRef);

  const { allowed: canRefresh } = useEntityPermission(
    catalogEntityRefreshPermission,
  );

  // ── Refresh ────────────────────────────────────────────────────────────────
  const entityLocation = entity.metadata.annotations?.[ANNOTATION_LOCATION];
  const allowRefresh =
    entityLocation?.startsWith("url:") || entityLocation?.startsWith("file:");

  const refreshEntity = useCallback(async () => {
    try {
      await catalogApi.refreshEntity(stringifyEntityRef(entity));
      alertApi.post({
        message: "Refresco programado, los cambios se aplicarán en breve.",
        severity: "info",
        display: "transient",
      });
    } catch (e) {
      errorApi.post(e as Error);
    }
  }, [catalogApi, entity, alertApi, errorApi]);

  // ── Editar sistema ─────────────────────────────────────────────────────────
  const handleEdit = () => {
    const metadata = entity.metadata;
    const spec = entity.spec as Record<string, unknown> | undefined;

    const formData = {
      systemName: metadata.name,
      title: metadata.title ?? "",
      description: metadata.description ?? "",
      owner: (spec?.owner as string) ?? "",
      domain: (spec?.domain as string) ?? "",
      type: (spec?.type as string) ?? "web-platform",
      criticality: (metadata.labels?.criticality as string) ?? "medium",
      jiraProjectKey: metadata.annotations?.["jira.project-key"] ?? "",
      confluenceUrl: metadata.annotations?.["confluence.url"] ?? "",
      cloudhealthAssetRefs:
        metadata.annotations?.["cloudhealth.io/asset-refs"] ?? "",
      tags: metadata.tags ?? [],
    };

    const queryParams = new URLSearchParams({
      formData: JSON.stringify(formData),
    });
    navigate(`/create/templates/default/edit-system?${queryParams}`);
  };

  // ── Subheader: enlace a fuente SCM + Confluence ──────────────────────────
  const entitySourceLocation = getEntitySourceLocation(
    entity,
    scmIntegrationsApi,
  );
  const confluenceUrl = entity.metadata.annotations?.["confluence.url"];
  const subheaderLinks = [
    ...(entitySourceLocation
      ? [
          {
            label: "Ver fuente",
            disabled: false,
            icon: (
              <ScmIntegrationIcon type={entitySourceLocation.integrationType} />
            ),
            href: entitySourceLocation.locationTargetUrl,
          },
        ]
      : []),
    ...(confluenceUrl
      ? [
          {
            label: "Documentación",
            disabled: false,
            icon: <OpenInNewIcon fontSize="small" />,
            href: confluenceUrl,
          },
        ]
      : []),
  ];

  return (
    <Card className={variant === "gridItem" ? classes.gridItemCard : undefined}>
      <CardHeader
        title="Sobre este sistema"
        action={
          <>
            {allowRefresh && canRefresh && (
              <IconButton
                aria-label="Refrescar entidad"
                title="Reprocesar entidad en el catálogo"
                onClick={refreshEntity}
              >
                <CachedIcon />
              </IconButton>
            )}
            <IconButton
              aria-label="Editar sistema"
              title="Editar los metadatos de este sistema"
              onClick={handleEdit}
            >
              <EditIcon />
            </IconButton>
          </>
        }
        subheader={
          subheaderLinks.length > 0 ? (
            <HeaderIconLinkRow links={subheaderLinks} />
          ) : undefined
        }
      />
      <Divider />
      <CardContent
        className={
          variant === "gridItem" ? classes.gridItemCardContent : undefined
        }
      >
        <AboutContent entity={entity} />
      </CardContent>
    </Card>
  );
};
